import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Warning, CircleNotch, WarningCircle, ArrowsClockwise, ArrowSquareOut } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { NavTabs } from "@/components/NavTabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feb 2026, user's explicit ask ("Is there a way to scan the receipts
// in SAP to find such cases where fulfilled quantity is equal to zero
// while delivery notification quantity is not equal to zero?") after
// the real missed case on shipment S000477/PO 27656 - our app showed
// "Posted" while SAP's own Inbound Delivery was still stuck "Not
// Released"/"Not Started" (Fulfilled Quantity 0 despite being
// notified). Admin-only (tenant-wide SAP scan), same "vendor_goods_
// receipt" page permission as the other GRN admin screens.
const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return format(parseISO(iso), "dd MMM yyyy, HH:mm");
  } catch {
    return iso;
  }
};

export default function GrnFulfillmentAuditPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const runScan = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: res } = await axios.get(`${API}/admin/grn/fulfillment-audit`);
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not run the fulfillment audit");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runScan();
  }, [runScan]);

  const results = data?.results || [];
  const missingDeliveryId = data?.missing_delivery_id || [];
  const multiPoShipments = data?.multi_po_shipments || [];

  return (
    <div className="min-h-screen bg-[#F2F4F7] font-sans" data-testid="grn-fulfillment-audit-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Warning size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">GRN Fulfillment Audit</span>
          </div>
        </div>
        <NavTabs />
      </header>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-5 py-5">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <Warning size={22} className="text-[#B42318]" />
            <h1 className="text-xl font-heading font-bold text-[#101828]" data-testid="grn-fulfillment-audit-title">GRN Fulfillment Audit</h1>
          </div>
          <button
            type="button"
            onClick={runScan}
            disabled={loading}
            data-testid="grn-fulfillment-audit-refresh-btn"
            className="flex items-center gap-1.5 text-sm font-semibold text-white bg-[#0E7C86] hover:bg-[#0B6670] disabled:opacity-60 rounded-sm px-3 py-1.5 transition-colors"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh Scan
          </button>
        </div>
        <p className="text-sm text-[#475467] mb-4 max-w-[900px]">
          Scans SAP tenant-wide for Inbound Deliveries still stuck "Not Released" / "Not Started" (Fulfilled Quantity 0
          despite being notified), then cross-checks the delivery IDs against this app's own shipments already marked
          "Posted" - every row below is a GRN this app shows as complete that SAP has not actually fulfilled yet. Use
          "Verify with SAP" on the Vendor Goods Receipt screen for that shipment to correct it.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-[#475467] text-sm" data-testid="grn-fulfillment-audit-loading">
            <CircleNotch size={16} className="animate-spin" /> Scanning SAP...
          </div>
        )}
        {error && (
          <div className="text-sm text-[#B91C1C] bg-[#E02424]/5 border border-[#E02424]/30 rounded-sm px-3 py-2" data-testid="grn-fulfillment-audit-error">
            <WarningCircle size={14} className="inline mr-1" /> {error}
          </div>
        )}

        {!loading && !error && data && (
          <div className="flex flex-wrap gap-4 mb-4" data-testid="grn-fulfillment-audit-summary">
            <div className="bg-white border border-[#D0D5DD] rounded-sm px-4 py-2">
              <div className="text-xs text-[#667085]">Stuck in SAP tenant-wide</div>
              <div className="text-lg font-heading font-bold text-[#101828]" data-testid="grn-fulfillment-audit-stuck-count">{data.stuck_delivery_count}</div>
            </div>
            <div className="bg-white border border-[#D0D5DD] rounded-sm px-4 py-2">
              <div className="text-xs text-[#667085]">Affecting our "Posted" GRNs</div>
              <div className="text-lg font-heading font-bold text-[#B42318]" data-testid="grn-fulfillment-audit-affected-count">{results.length}</div>
            </div>
            <div className="bg-white border border-[#D0D5DD] rounded-sm px-4 py-2">
              <div className="text-xs text-[#667085]">Posted, missing a SAP Delivery #</div>
              <div className="text-lg font-heading font-bold text-[#B45309]" data-testid="grn-fulfillment-audit-missing-id-count">{missingDeliveryId.length}</div>
            </div>
            <div className="bg-white border-2 border-[#B42318] rounded-sm px-4 py-2">
              <div className="text-xs text-[#667085]">Shipments with 2+ affected POs</div>
              <div className="text-lg font-heading font-bold text-[#B42318]" data-testid="grn-fulfillment-audit-multi-po-count">{multiPoShipments.length}</div>
            </div>
            <div className="bg-white border border-[#D0D5DD] rounded-sm px-4 py-2">
              <div className="text-xs text-[#667085]">Last scanned</div>
              <div className="text-sm font-data text-[#101828] mt-0.5">{fmtDate(data.scanned_at)}</div>
            </div>
          </div>
        )}

        {!loading && !error && multiPoShipments.length > 0 && (
          <div className="mb-6" data-testid="grn-fulfillment-audit-multi-po-section">
            <div className="flex items-center gap-2 mb-1">
              <Warning size={18} className="text-[#B42318]" weight="fill" />
              <h2 className="text-base font-heading font-bold text-[#B42318]" data-testid="grn-fulfillment-audit-multi-po-title">
                Highest Risk: Shipments With Multiple Affected POs
              </h2>
            </div>
            <p className="text-sm text-[#475467] mb-3 max-w-[900px]">
              A shipment with 2+ affected PO lines (stuck and/or missing a SAP Delivery #) across different POs or
              inbound IDs is the exact pattern seen on S001741, where a combined delivery and separate legacy per-PO
              deliveries both existed for the same shipment and caused a duplicate, over-received posting in SAP.
              These deserve priority manual review.
            </p>
            <div className="bg-white border-2 border-[#B42318]/40 rounded-sm overflow-x-auto">
              <table className="border-collapse w-full text-xs">
                <thead className="bg-[#FEF3F2] text-[#B42318] text-xs font-bold font-heading tracking-wide">
                  <tr>
                    <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Approved</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Shipment</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Supplier</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Bill Number</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Affected POs</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">SAP Delivery #s</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {multiPoShipments.map((r, i) => (
                    <tr key={`${r.doc_code}-${i}`} className="bg-white odd:bg-[#FEF3F2]/40 align-top" data-testid={`grn-fulfillment-audit-multi-po-row-${i}`}>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap font-data">{fmtDate(r.approved_at)}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-data font-semibold text-[#004B87]">{r.doc_code}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`grn-fulfillment-audit-multi-po-supplier-${i}`}>{r.supplier_name || "-"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5">{r.supplier_doc_num || "-"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{(r.po_numbers || []).join(", ")}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{(r.inbound_delivery_ids || []).join(", ") || "-"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap">
                        <Link
                          to="/admin/grn-approval"
                          data-testid={`grn-fulfillment-audit-multi-po-open-${i}`}
                          className="flex items-center gap-1 text-[#0E7C86] font-semibold hover:underline"
                        >
                          Open GRN <ArrowSquareOut size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && results.length === 0 && (
          <div className="text-sm text-[#027A48] bg-[#ECFDF3] border border-[#027A48]/30 rounded-sm px-3 py-6 text-center" data-testid="grn-fulfillment-audit-empty">
            No "Posted" GRN is currently pointing at an unfulfilled SAP delivery.
          </div>
        )}

        {!loading && !error && results.length > 0 && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto mb-6" data-testid="grn-fulfillment-audit-table">
            <table className="border-collapse w-full text-xs">
              <thead className="bg-[#EAECF0] text-[#344054] text-xs font-bold font-heading tracking-wide">
                <tr>
                  <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Approved</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Shipment</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Supplier</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Bill Number</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">PO</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Product(s)</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">SAP Delivery #</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Stuck Since</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Last Event</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={`${r.doc_code}-${r.po_number}-${r.inbound_delivery_id}-${i}`} className="bg-white odd:bg-[#F9FAFB] align-top" data-testid={`grn-fulfillment-audit-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap font-data">{fmtDate(r.approved_at)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data font-semibold text-[#004B87]">{r.doc_code}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`grn-fulfillment-audit-supplier-${i}`}>{r.supplier_name || "-"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.supplier_doc_num || "-"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{r.po_number}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{(r.products || []).join(", ") || "-"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-data whitespace-nowrap">{r.inbound_delivery_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap font-data">{fmtDate(r.stuck_since)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 max-w-[320px]" data-testid={`grn-fulfillment-audit-event-${i}`}>
                      {(r.events && r.events[r.events.length - 1]) || "-"}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap">
                      <Link
                        to="/admin/grn-approval"
                        data-testid={`grn-fulfillment-audit-open-${i}`}
                        className="flex items-center gap-1 text-[#0E7C86] font-semibold hover:underline"
                      >
                        Open GRN <ArrowSquareOut size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="flex items-center gap-2 mb-1 mt-2">
              <Warning size={18} className="text-[#B45309]" />
              <h2 className="text-base font-heading font-bold text-[#101828]" data-testid="grn-fulfillment-audit-missing-id-title">Posted but Missing a SAP Delivery #</h2>
            </div>
            <p className="text-sm text-[#475467] mb-3 max-w-[900px]">
              These GRNs were marked "Posted" but this app never captured a SAP Inbound Delivery number for them at
              all, so they can't be cross-checked against SAP's stuck list above - their real fulfillment status in
              SAP is unknown. Every scan already re-checks SAP for each row below and auto-heals it (same as
              clicking "Fetch from SAP" on the Vendor Goods Receipt screen) the moment SAP shows a genuine
              completed delivery - rows only stay listed here while SAP still hasn't finished processing them.
            </p>
            {missingDeliveryId.length === 0 ? (
              <div className="text-sm text-[#027A48] bg-[#ECFDF3] border border-[#027A48]/30 rounded-sm px-3 py-6 text-center" data-testid="grn-fulfillment-audit-missing-id-empty">
                Every "Posted" GRN has a SAP Delivery # captured.
              </div>
            ) : (
              <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-x-auto" data-testid="grn-fulfillment-audit-missing-id-table">
                <table className="border-collapse w-full text-xs">
                  <thead className="bg-[#EAECF0] text-[#344054] text-xs font-bold font-heading tracking-wide">
                    <tr>
                      <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Approved</th>
                      <th className="border border-[#D0D5DD] p-1.5 text-left">Shipment</th>
                      <th className="border border-[#D0D5DD] p-1.5 text-left">Supplier</th>
                      <th className="border border-[#D0D5DD] p-1.5 text-left">Bill Number</th>
                      <th className="border border-[#D0D5DD] p-1.5 text-left">PO</th>
                      <th className="border border-[#D0D5DD] p-1.5 text-left">Product(s)</th>
                      <th className="border border-[#D0D5DD] p-1.5 text-left">Last Event</th>
                      <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingDeliveryId.map((r, i) => (
                      <tr key={`${r.doc_code}-${r.po_number}-${i}`} className="bg-white odd:bg-[#F9FAFB] align-top" data-testid={`grn-fulfillment-audit-missing-id-row-${i}`}>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap font-data">{fmtDate(r.approved_at)}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 font-data font-semibold text-[#004B87]">{r.doc_code}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`grn-fulfillment-audit-missing-id-supplier-${i}`}>{r.supplier_name || "-"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5">{r.supplier_doc_num || "-"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{r.po_number}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 font-data">{(r.products || []).join(", ") || "-"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 max-w-[320px]" data-testid={`grn-fulfillment-audit-missing-id-event-${i}`}>
                          {(r.events && r.events[r.events.length - 1]) || "-"}
                        </td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 whitespace-nowrap">
                          <Link
                            to="/admin/grn-approval"
                            data-testid={`grn-fulfillment-audit-missing-id-open-${i}`}
                            className="flex items-center gap-1 text-[#0E7C86] font-semibold hover:underline"
                          >
                            Open GRN <ArrowSquareOut size={12} />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
